import React, { useContext } from "react"
import { observer } from "mobx-react"
import {
  Badge,
  Button,
  Center,
  Group,
  Highlight,
  Loader,
  NavLink,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title
} from "@mantine/core"
import { IconSearch } from "@tabler/icons-react"
import { AppWorkspaceContext } from "../../stores/AppWorkspace"
import { MarkdownDoc } from "../MarkdownDoc"
import { CopyAnchorButton } from "../CopyAnchorButton"
import { API_PLATFORM_FILTERS, CATALOG, highlightTerms } from "../../../docs/catalog"
import type { ApiPlatform } from "../../../docs/types"

const platformLabel = (platform: string) =>
  API_PLATFORM_FILTERS.find((item) => item.id === platform)?.label ?? platform

export const ApiDocsPage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  const docs = workspace.docs
  const terms = highlightTerms(docs.query)

  return (
    <Stack gap="lg" py="md">
      <div>
        <Text className="or-eyebrow">Reference</Text>
        <Title>API docs</Title>
        <Text c="dimmed" mt={6} maw={720}>
          Wallet client, host, HTTP routes and their wire types, framework adapters, browser
          packages, CLI, the Rails engine, and the Python and PHP packages. Every route shows
          what to send and what comes back; every response type is its own entry.
        </Text>
      </div>

      <TextInput
        size="md"
        leftSection={<IconSearch size={18} />}
        placeholder="Search functions, routes, types, Rails hooks…"
        value={docs.query}
        onChange={(event) => workspace.setDocsQuery(event.currentTarget.value)}
        autoFocus
      />

      <Group gap="xs">
        {API_PLATFORM_FILTERS.map((item) => (
          <Button
            key={item.id}
            size="compact-sm"
            variant={docs.platform === item.id ? "filled" : "light"}
            onClick={() => workspace.setDocsPlatform(item.id as "all" | ApiPlatform)}
          >
            {item.label}
          </Button>
        ))}
      </Group>

      {/* The reference is its own chunk; until it lands there is nothing to count. */}
      {docs.loading ? (
        <Center py="xl">
          <Loader color="orGreen" />
        </Center>
      ) : (
        <>
          <Text size="sm" c="dimmed">
            {docs.resultCount} matching {docs.resultCount === 1 ? "symbol" : "symbols"}
            {docs.query.trim() ? ` for “${docs.query.trim()}”` : ""}. Use the link icon to copy an
            exact URL to a function or type.
          </Text>

          <div className="or-docs-layout">
            <ScrollArea.Autosize mah="calc(100vh - 220px)" className="or-docs-toc">
              <Stack gap={4} pr="sm">
                {docs.toc.map((group) => (
                  <div key={`${group.platform}:${group.section}`}>
                    <Text size="xs" tt="uppercase" fw={700} c="dimmed" mt="sm" mb={4}>
                      {group.section}
                    </Text>
                    {group.items.map((item) => (
                      <NavLink
                        key={item.id}
                        href={`#${item.anchor}`}
                        label={
                          terms.length ? (
                            <Highlight highlight={terms}>{item.title}</Highlight>
                          ) : (
                            item.title
                          )
                        }
                        active={docs.selectedAnchor === item.anchor}
                        onClick={(event) => {
                          event.preventDefault()
                          workspace.setHash(item.anchor)
                          document
                            .getElementById(item.anchor)
                            ?.scrollIntoView({ behavior: "smooth", block: "start" })
                        }}
                      />
                    ))}
                  </div>
                ))}
              </Stack>
            </ScrollArea.Autosize>

            <Stack gap="md">
              {docs.visibleEntries.length === 0 ? (
                <Paper withBorder p="xl">
                  <Text>No API symbols matched that search.</Text>
                </Paper>
              ) : (
                docs.sections.map((group) => (
                  <Stack key={`${group.platform}:${group.section}`} gap="md">
                    <Group gap="xs">
                      <Title order={3}>{group.section}</Title>
                      <Badge variant="light">{platformLabel(group.platform)}</Badge>
                    </Group>
                    {group.entries.map((entry) => (
                      <Paper
                        key={entry.id}
                        id={entry.anchor}
                        withBorder
                        p="md"
                        radius="md"
                        className="or-api-entry"
                      >
                        <Group justify="space-between" align="flex-start" mb="sm" wrap="nowrap">
                          <div>
                            <Group gap={8} wrap="wrap">
                              <Title order={4} className="or-heading">
                                {terms.length ? (
                                  <Highlight highlight={terms}>{entry.title}</Highlight>
                                ) : (
                                  entry.title
                                )}
                              </Title>
                              <Badge size="sm" variant="outline">
                                {entry.kind}
                              </Badge>
                            </Group>
                            {entry.signature && !entry.markdown.trimStart().startsWith("```") ? (
                              // The signature is the first line of the entry's code
                              // block; when that block opens the entry it is already
                              // on screen, so the extra line would only repeat it.
                              <Text size="sm" c="dimmed" ff="monospace" mt={4}>
                                {entry.signature}
                              </Text>
                            ) : null}
                          </div>
                          <CopyAnchorButton anchor={entry.anchor} path="/api_docs" />
                        </Group>
                        {docs.query.trim() ? (
                          <Text size="sm" mb="sm">
                            <Highlight highlight={terms}>{entry.excerpt}</Highlight>
                          </Text>
                        ) : null}
                        <MarkdownDoc markdown={entry.markdown} />
                      </Paper>
                    ))}
                  </Stack>
                ))
              )}
              <Text size="xs" c="dimmed">
                Synced from OpenReceive {CATALOG.sourceVersion}
                {CATALOG.sourceCommit ? ` @ ${CATALOG.sourceCommit}` : ""} on{" "}
                {CATALOG.generatedAt.slice(0, 10)}.
              </Text>
            </Stack>
          </div>
        </>
      )}
    </Stack>
  )
})
